// Browsers cannot decode MPEG-1/2 program streams (.mpeg/.mpg) in a <video> element, so frame
// picking and screenshot capture fail for them. For those files we transcode a small MP4 preview
// in the browser with ffmpeg.wasm; the original file is still what gets uploaded.
const MPEG_EXTENSIONS = ['.mpeg', '.mpg']

const PREVIEW_MAX_HEIGHT = 480

export const needsPlayablePreview = (file: Pick<File, 'name' | 'type'>): boolean => {
  const name = file.name?.toLowerCase() ?? ''
  return file.type === 'video/mpeg' || MPEG_EXTENSIONS.some(extension => name.endsWith(extension))
}

const transcodeToMp4Preview = async (file: File): Promise<Blob> => {
  const [{ FFmpeg }, { fetchFile }, { default: coreURL }, { default: wasmURL }] = await Promise.all([
    import('@ffmpeg/ffmpeg'),
    import('@ffmpeg/util'),
    import('@ffmpeg/core?url'),
    import('@ffmpeg/core/wasm?url'),
  ])

  const ffmpeg = new FFmpeg()
  try {
    await ffmpeg.load({ coreURL, wasmURL })
    await ffmpeg.writeFile('input.mpeg', await fetchFile(file))
    const exitCode = await ffmpeg.exec([
      '-i',
      'input.mpeg',
      '-an',
      '-vf',
      `scale=-2:'min(${PREVIEW_MAX_HEIGHT},ih)'`,
      '-c:v',
      'libx264',
      '-preset',
      'ultrafast',
      '-pix_fmt',
      'yuv420p',
      '-movflags',
      '+faststart',
      'preview.mp4',
    ])
    if (exitCode !== 0) throw new Error(`ffmpeg exited with code ${exitCode}`)
    const data = (await ffmpeg.readFile('preview.mp4')) as Uint8Array
    return new Blob([data], { type: 'video/mp4' })
  } finally {
    ffmpeg.terminate()
  }
}

const previewCache = new WeakMap<File, Promise<Blob>>()

export const getPlayableVideoBlob = (file: File): Promise<Blob> => {
  if (!needsPlayablePreview(file)) return Promise.resolve(file)

  let preview = previewCache.get(file)
  if (!preview) {
    preview = transcodeToMp4Preview(file)
    preview.catch(() => previewCache.delete(file))
    previewCache.set(file, preview)
  }
  return preview
}
